import { useEffect, useRef, useState } from 'react'

import { useInView } from 'react-intersection-observer'

import type { HeaderRefCallback } from './types'

const useStickyHeader = (stickyThreshold: number): [HeaderRefCallback, boolean] => {
    const isIntersectionObserverSupported =
        typeof window !== 'undefined' && typeof window.IntersectionObserver !== 'undefined'

    const intersectionObserverResult = useStickyHeaderWithIntersectionObserver(
        isIntersectionObserverSupported,
        stickyThreshold,
    )
    const scrollEventListenerResult = useStickyHeaderWithScrollEventListener(
        isIntersectionObserverSupported,
        stickyThreshold,
    )

    return isIntersectionObserverSupported ? intersectionObserverResult : scrollEventListenerResult
}

const useStickyHeaderWithIntersectionObserver = (isIntersectionObserverSupported: boolean, stickyThreshold: number) => {
    const [headerRefCallback, isHeaderInView] = useInView({
        initialInView: true,
        rootMargin: `${-stickyThreshold - 1}px 0px 0px 0px`,
        skip: !isIntersectionObserverSupported,
    })
    const isHeaderSticky = !isHeaderInView

    return [headerRefCallback, isHeaderSticky] as [HeaderRefCallback, boolean]
}

const useStickyHeaderWithScrollEventListener = (isIntersectionObserverSupported: boolean, stickyThreshold: number) => {
    const [isHeaderSticky, setIsHeaderSticky] = useState(false)
    const headerRef = useRef<Element | null | undefined>(null)

    const headerRefCallback = (node?: Element | null | undefined) => {
        headerRef.current = node
    }

    useEffect(() => {
        if (!isIntersectionObserverSupported) {
            const onScroll = () => {
                if (headerRef.current) {
                    const boundingRect = headerRef.current.getBoundingClientRect()
                    if (!isHeaderSticky && boundingRect.top <= stickyThreshold) {
                        setIsHeaderSticky(true)
                    } else if (isHeaderSticky && boundingRect.top > stickyThreshold) {
                        setIsHeaderSticky(false)
                    }
                }
            }
            window.addEventListener('scroll', onScroll)
            return () => window.removeEventListener('scroll', onScroll)
        }
    }, [isIntersectionObserverSupported, stickyThreshold, isHeaderSticky, headerRef])

    return [headerRefCallback, isHeaderSticky] as [HeaderRefCallback, boolean]
}

export default useStickyHeader
